/* Copyright 2026 Element Creations Ltd.
SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial */
import {useEffect,useState,type FC} from "react";
import {createClient} from "matrix-js-sdk";
import type {Session} from "../ClientContext";
import {backendFetch} from "../utils/backend";
import styles from "./CabinetInviteRedirect.module.css";
import {preferredLanguage} from "../utils/appLanguage";
import {joinTarget} from "./joinSession";
export async function cabinetInviteTarget(signal:AbortSignal):Promise<string> {
 const admin=import.meta.env.VITE_ADMIN_URL,guest=import.meta.env.VITE_GUEST_URL;
 if(!admin||!guest||window.location.origin!==new URL(admin).origin||window.parent!==window)throw Error("Unexpected cabinet context");
 const params=new URLSearchParams(window.location.hash.replace(/^#\??/,"")),relative=params.get("target"),challenge=params.get("challenge"),probe=params.get("probe");
 if(!relative||!relative.startsWith("/")||relative.startsWith("//")||!challenge||!/^[A-Za-z0-9_-]{43}$/.test(challenge)||!probe||! /^[a-f0-9-]{36}$/.test(probe))throw Error("Invalid invitation check");
 const target=new URL(relative,guest);
 if(target.origin!==new URL(guest).origin||!['/room/','/room','/'].includes(target.pathname))throw Error("Unexpected invitation destination");
 let session:Session|undefined;
 try {const raw=localStorage.getItem("matrix-auth-store");session=raw?JSON.parse(raw):undefined;}catch{}
 if(session?.access_token) {
  const client=createClient({baseUrl:session.homeserver_url!,accessToken:session.access_token,userId:session.user_id,deviceId:session.device_id});
  const meResponse=await fetch("/api/me",{headers:{Authorization:`Bearer ${session.access_token}`},signal});
  if(meResponse.ok) {
   const me=await meResponse.json();
   if(me.userId===session.user_id && ["owner","organizer"].includes(me.role)) {
    const response=await backendFetch(client,"/api/auth/join-session/link",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({sessionOnly:true,challenge}),signal});
    const {code}=await response.json();const staff=new URL(joinTarget(relative,guest,session.user_id,code));
    const index=staff.hash.indexOf('?'),fragment=new URLSearchParams(staff.hash.slice(index+1));fragment.set("accountOnly","1");
    staff.hash=staff.hash.slice(0,index)+"?"+fragment.toString();return staff.href;
   }
  } else if(![401,403].includes(meResponse.status))throw Error("Cabinet session check unavailable");
 }
 target.searchParams.set("cabinetCheck",probe);return target.href;
}
export const CabinetInviteRedirect:FC=()=>{
 const [failed,setFailed]=useState(false);
 useEffect(()=>{
  const controller=new AbortController();
  void cabinetInviteTarget(controller.signal).then(target=>{if(!controller.signal.aborted)window.location.replace(target);}).catch(()=>{
   if(!controller.signal.aborted){document.body.classList.remove("no-theme");setFailed(true);}
  });return()=>controller.abort();
 },[]);
 if(!failed)return null;
 const ru=(preferredLanguage()||navigator.language).startsWith("ru");
 let retry=import.meta.env.VITE_ADMIN_URL||"/";
 try {const p=new URLSearchParams(window.location.hash.replace(/^#\??/,"")),target=new URL(p.get("target")||"/",import.meta.env.VITE_GUEST_URL);
  if(target.origin===new URL(import.meta.env.VITE_GUEST_URL!).origin && ["/room/","/room","/"].includes(target.pathname))retry=target.href;
 }catch{}
 return <main className={styles.page}><section className={styles.card} role="alert">
  <h1>{ru?"Не удалось проверить аккаунт":"Could not verify your account"}</h1>
  <p>{ru?"Проверь подключение и попробуй открыть приглашение ещё раз.":"Check your connection and try opening the invitation again."}</p>
  <a className="glass-button" href={retry}>{ru?"Попробовать ещё раз":"Try again"}</a>
  <a href={import.meta.env.VITE_ADMIN_URL||"/"}>{ru?"В кабинет":"Back to cabinet"}</a>
 </section></main>;
};
